import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, CheckCircle, ArrowRight, Home as HomeIcon, Trash2, Sofa,
  Building2, Package, Zap, Hammer, TreePine, Bath, Refrigerator
} from 'lucide-react';

const useScrollAnimation = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

const services = [
  {
    icon: Trash2,
    title: 'General Rubbish Removal',
    desc: 'Quick and efficient removal of all types of general waste. Whether it\'s bagged household rubbish, bulky items or mixed waste — we handle it all with minimal disruption to you.',
    items: ['Bagged Waste Collection', 'Bulky Item Removal', 'Mixed Waste', 'Black Bag Collections', 'Seasonal Clear-Outs'],
    badge: 'Most Popular',
    badgeColor: 'bg-green-600',
  },
  {
    icon: HomeIcon,
    title: 'Property Clearance',
    desc: 'Full property clearance for houses, flats and offices. Perfect for landlords, estate agents, or anyone needing a complete clear-out before sale, rent or renovation.',
    items: ['Full House Clear-Out', 'Flat & Apartment Clearance', 'Bereavement Clearances', 'Pre-Sale Clearance', 'Rental Property Clean-Out'],
    badge: 'Residential',
    badgeColor: 'bg-blue-600',
  },
  {
    icon: Sofa,
    title: 'Furniture Removal',
    desc: 'Safe removal and disposal of all types of furniture. From single sofas to full room sets — we take it all away without you lifting a finger.',
    items: ['Sofas & Couches', 'Beds & Mattresses', 'Wardrobes & Drawers', 'Tables & Chairs', 'Office Furniture'],
    badge: 'Quick Removal',
    badgeColor: 'bg-purple-600',
  },
  {
    icon: Refrigerator,
    title: 'Appliance Removal',
    desc: 'Responsible removal and disposal of old and unwanted white goods and appliances. We ensure all items are properly recycled in line with WEEE regulations.',
    items: ['Fridges & Freezers', 'Washing Machines', 'Dishwashers & Dryers', 'Ovens & Cookers', 'Televisions & Electronics'],
    badge: 'Eco-Friendly',
    badgeColor: 'bg-teal-600',
  },
  {
    icon: Package,
    title: 'Estate Clean Out',
    desc: 'Sensitive and professional clearance of estates following bereavement. We handle the process with care, discretion and respect during difficult times.',
    items: ['Full Estate Clearance', 'Donation Sorting', 'Item Valuation Assistance', 'Sensitive & Discreet Service', 'Same Week Availability'],
    badge: 'Compassionate',
    badgeColor: 'bg-rose-600',
  },
  {
    icon: Zap,
    title: 'Hoarder Clear Out',
    desc: 'Non-judgmental, patient and thorough clean-outs for hoarder properties. We work sensitively at a pace that suits you, ensuring items are safely removed.',
    items: ['Non-Judgmental Service', 'Multi-Day Jobs Welcome', 'Partial Clear-Outs', 'Item Sorting Assistance', 'Deep Clean Ready'],
    badge: 'Specialist',
    badgeColor: 'bg-orange-600',
  },
  {
    icon: Building2,
    title: 'Commercial Rubbish Removal',
    desc: 'Fast, discreet commercial waste clearance for businesses, offices and retail premises. We work around your schedule to minimise disruption.',
    items: ['Office Waste Removal', 'Retail Fit-Out Waste', 'Restaurant & Café Clearance', 'Warehouse Clearance', 'Regular Collections'],
    badge: 'Commercial',
    badgeColor: 'bg-gray-700',
  },
  {
    icon: Hammer,
    title: 'Light Demolition',
    desc: 'Careful light demolition and removal of garden structures, outbuildings and more. We demolish, clear and remove all debris leaving a clean space.',
    items: ['Shed Dismantling & Removal', 'Garage Demolition', 'Garden Structure Removal', 'Decking Removal', 'Debris & Rubble Clearance'],
    badge: 'Specialist',
    badgeColor: 'bg-amber-600',
  },
  {
    icon: Bath,
    title: 'Hot Tub Removal',
    desc: 'Specialist removal of old and unwanted hot tubs. We handle draining, dismantling and full disposal — no mess left behind.',
    items: ['Hot Tub Draining', 'Dismantling & Removal', 'Surrounding Debris Clear', 'Full Disposal', 'Garden Restoration Ready'],
    badge: 'Specialist',
    badgeColor: 'bg-cyan-600',
  },
  {
    icon: TreePine,
    title: 'Garden & Outdoor Waste',
    desc: 'Clearance of garden waste, green waste and outdoor rubbish. Keep your garden looking pristine with our efficient garden waste removal service.',
    items: ['Green & Garden Waste', 'Fence & Timber Removal', 'Old Sheds & Structures', 'Patio & Paving', 'Garden Tool Disposal'],
    badge: 'Outdoor',
    badgeColor: 'bg-lime-600',
  },
];

export default function Services() {
  useScrollAnimation();

  return (
    <div>
      {/* PAGE HEADER */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-green-400 font-bold text-sm uppercase tracking-widest">What We Offer</span>
          <h1 className="text-5xl font-black text-white mt-3 mb-4">Our Services</h1>
          <p className="text-green-200 text-lg max-w-2xl mx-auto">
            Comprehensive waste removal solutions for homes, businesses and everything in between. Fast, professional and always at a fair price.
          </p>
        </div>
      </section>

      {/* QUICK PROMISES */}
      <section className="bg-green-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {['Free Estimates', 'Same Day Service', 'No Hidden Fees', 'Fully Licensed'].map((p) => (
              <div key={p} className="flex items-center justify-center gap-2 text-white">
                <CheckCircle size={18} className="text-green-300" />
                <span className="font-semibold text-sm">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(({ icon: Icon, title, desc, items, badge, badgeColor }) => (
              <div
                key={title}
                className="bg-white rounded-2xl p-6 shadow-md card-hover border border-gray-100 animate-on-scroll"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-green-600 rounded-xl p-3">
                    <Icon size={22} className="text-white" />
                  </div>
                  <span className={`${badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {badge}
                  </span>
                </div>
                <h3 className="font-black text-gray-900 text-lg mb-3">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{desc}</p>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <li key={item} className="text-gray-600 text-sm flex items-center gap-2">
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 animate-on-scroll">
            <span className="text-green-600 font-bold text-sm uppercase tracking-wider">Simple Process</span>
            <h2 className="text-4xl font-black text-gray-900 mt-2 mb-4">How It Works</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Getting rid of your rubbish couldn't be simpler. Here's how we work:</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Call or Message', desc: 'Contact Chris by phone or WhatsApp for a quick chat about your needs.' },
              { step: '02', title: 'Free Quote', desc: 'We\'ll give you a fair, honest no-obligation quote with no hidden fees.' },
              { step: '03', title: 'We Arrive', desc: 'Our team arrives at your agreed time — punctual and ready to work.' },
              { step: '04', title: 'All Done!', desc: 'We load, clear and responsibly dispose of everything. Job done!' },
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center animate-on-scroll">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-black text-lg">{step}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-base mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hero-gradient py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-white mb-4">Need a Service? Call Now!</h2>
          <p className="text-green-200 text-lg mb-8">Free estimates, same day service, no hidden fees. Serving Dublin, Meath, Louth & Kildare.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:0852730131"
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-white font-black px-10 py-5 rounded-2xl transition-all text-xl"
            >
              <Phone size={24} />
              085 273 0131
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/40 text-white font-bold px-10 py-5 rounded-2xl transition-all text-lg"
            >
              Get a Free Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
